import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Status } from "../../../Constants/Consts.ts";
import { RootState } from "../../../store/store";
import { ROLES } from "../../../utils/roles.ts";
import roleCheck from "../../../utils/roleCheck.ts";
import SyncManager from "../../SyncManager/SyncManager";

// UI ELEMENTS
import { AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import { BiArrowBack } from "react-icons/bi";
import { BsPinMap } from "react-icons/bs";
import { FaRegCircleUser } from "react-icons/fa6";
import { GoChecklist } from "react-icons/go";
import { HiExternalLink } from "react-icons/hi";
import { IoIosAddCircleOutline } from "react-icons/io";
import { MdOutlineHomeWork, MdOutlinePostAdd, MdSecurity } from "react-icons/md";
import { PiClockCountdownFill, PiUsersThreeDuotone } from "react-icons/pi";
import { RxReload } from "react-icons/rx";
import { VscChecklist } from "react-icons/vsc";
import LogoutButton from "../../User/LogoutButton.tsx";
import andriodIcon from "/android-chrome-512x512.png";
import useOnline from "../../../Helpers/CustomHooks/userOnlineCheck.ts";
import { BsCardChecklist } from "react-icons/bs";
import { LuSheet } from "react-icons/lu";

const Navbar = () => {
    const [nav, setNav] = useState(false);
    const navigate = useNavigate();
    const isOnline = useOnline();

    const { SelectedUser } = useSelector(
        (state: RootState) => state.SelectedUser
    );
    const { backClickHandler } = useSelector(
        (state: RootState) => state.Utilities
    );
    const PendingPunchlistCount = useSelector(
        (state: RootState) => state.Utilities.pendingPunchListCount
    );

    const filterMenuItems = (groupId: number) => {
        return menuItems.filter(item =>
            item.groupId === groupId && roleCheck(SelectedUser?.role, item.roles));
    };

    const menuRef = useRef<HTMLDivElement>(null);
    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setNav(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    // ACCESS CODE
    const isRoleSubcontractor = SelectedUser.role === 'External';
    const isDataMissing = !SelectedUser.projectNumber && !SelectedUser.discipline;
    const hasAccess = !isDataMissing || isRoleSubcontractor;
    const menuItems = useMemo(() => [
        {
            groupId: 1,
            icon: <MdOutlineHomeWork size={20} className="mr-4 text-orange-1000" />,
            text: "Home",
            id: "home",
            path: "/",
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External],
        },
        {
            groupId: 1,
            icon: <VscChecklist size={20} className="mr-4 text-orange-1000" />,
            text: "Checklists",
            id: "checklists",
            // path: "/Sequences", OLD PATH
            path: "/TasksDD", // NEW PATH
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External],
        },
        {
            groupId: 1,
            icon:
                <div className="relative">
                    <GoChecklist size={28} className="mr-4 text-orange-1000" />
                    <span className="absolute top-0.5 right-4 grid min-h-[20px] min-w-[30px] translate-x-2/4 -translate-y-2/4 place-items-center rounded-full bg-red-600 text-xs text-white">
                        {PendingPunchlistCount}
                    </span>
                </div>,
            text: "Punch Lists",
            id: "punch-list",
            path: "/PunchList",
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 1,
            icon: <MdOutlinePostAdd size={20} className="mr-4 text-orange-1000" />,
            text: "Add New Punch List Issue",
            id: "add-new-punch-list-issue",
            path: "/PunchList/Form/Discipline/1",
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 1,
            id: "ready-for-reInspection",
            icon: <RxReload size={20} className="mr-4 text-orange-1000" />,
            text: "Ready For Re-Inspection",
            path: `/PunchListDiscipline/${SelectedUser.disciplineId}/view/${Status.ReadyForReInspection}/assignedDisciplineId`,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 1,
            icon: <BsPinMap size={20} className="mr-4 text-orange-1000" />,
            text: "Maps / Drawings",
            id: "maps-drawings",
            path: `/DrawingLayouts`,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 1,
            icon: <IoIosAddCircleOutline size={20} className="mr-4 text-orange-1000" />,
            text: "Adhoc Checklist Item",
            path: "/AdHocUnits",
            id: "ad-hoc-units",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 1,
            icon: <FaRegCircleUser size={20} className=" mr-4 text-orange-1000" />,
            text: "Edit Project Assignment",
            path: `/AllUsers/Edit/${SelectedUser?.assignmentUuid}`,
            notAvailableOffline: true,
            id: "edit-project-assignment",
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 2,
            icon: <PiUsersThreeDuotone size={20} className="mr-4" />,
            text: "All Users",
            path: "/AllUsers",
            id: "all-users",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External]
        },
        {
            groupId: 2,
            icon: <PiClockCountdownFill size={20} className="mr-4" />,
            text: "Projects",
            id: "projects",
            path: "/Projects",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External],
        },
        {
            groupId: 2,
            icon: <BsCardChecklist size={20} className="mr-4" />,
            text: "Models",
            id: "models",
            path: "/ModelInspections",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin],
        },
        {
            groupId: 2,
            icon: <BsCardChecklist size={20} className="mr-4" />,
            text: "Checklist Manager",
            id: "checklist-manager",
            path: "/ChecklistsManagement",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin],
        },
        {
            groupId: 2,
            icon: <LuSheet size={20} className="mr-4" />,
            text: "All Changelogs",
            id: "all-changelogs",
            path: "/ChangeLogs",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External],
        },
        {
            groupId: 2,
            icon: <HiExternalLink size={20} className="mr-4" />,
            text: "Training (SharePoint)",
            id: "training-sharepoint",
            link: 'https://kionnam.sharepoint.com/sites/DematicUnite/SitePages/Training.aspx',
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External],
        },
        {
            groupId: 2,
            icon: <MdSecurity size={20} className="mr-4" />,
            text: "Role Permissions",
            id: "role-permissions",
            path: "/RolePermissions",
            notAvailableOffline: true,
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager],
        },
        {
            groupId: 2,
            icon: <PiClockCountdownFill size={20} className="mr-4" />,
            text: "Pending Sync Items",
            id: "pending-sync-items",
            path: "/PendingSyncItems",
            roles: [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager, ROLES.User, ROLES.External],
        },
    ], [SelectedUser, PendingPunchlistCount]);

    return (
        <div className='bg-white dark:bg-gray-1000 p-2.5'>
                    <div className="flex justify-between items-center gap-4">
                        {/* Hamburger icon and UNITE Logo */}
                        {hasAccess ? <div className="flex items-center gap-4">
                            <div data-testid="navbar-hamburger-icon" onClick={(e) => { e.stopPropagation(); setNav(true); }} className="cursor-pointer">
                                <AiOutlineMenu size={30} />
                            </div>
                            <h2 data-testid="navbar-logo" onClick={() => navigate("/")} className="text-2xl flex items-center cursor-pointer">
                                <img src={andriodIcon} alt="Logo" className="w-8 h-8 mr-2 hidden md:inline-block" />
                                <span className="font-bold hidden md:inline-block">UNITE</span>
                            </h2>
                            {/* Back arrow component */}
                            <div data-testid="navbar-back-arrow" >
                                <BiArrowBack className="cursor-pointer scale-150" title="Back" onClick={() => backClickHandler ? backClickHandler() : navigate(-1)} />
                            </div>
                        </div> : <div></div>}
                        {/* SyncManager and logout components */}
                        <div className="flex items-center gap-10 ">
                            {hasAccess &&
                                <>
                                    <SyncManager />
                                    {/* <Downloaded /> */}
                                </>
                            }
                            <LogoutButton />
                            <div data-testid="navbar-logout-button" className="absolute right-3">
                            </div>
                        </div>
                    </div>
                    {nav ? (

                        <div data-testid="navbar-backdrop" className="bg-black/80 fixed w-full h-screen z-10 top-0 left-0"></div>

                    ) : (
                        ""
                    )}

                    {/* Side nav menu */}

                    <div data-testid="navbar-side-menu">
                        <div
                            ref={menuRef}
                            className={`
                                ${nav
                                    ? "fixed top-0 left-0 w-[300px] h-screen bg-white z-10 duration-300 dark:bg-gray-1000"
                                    : "fixed top-0 left-[-100%] w-[300px] h-screen bg-white z-10 duration-300 dark:bg-gray-1000"
                                } overflow-y-scroll`}
                        >
                            <AiOutlineClose data-testid="navbar-close-icon" onClick={() => setNav(!nav)} size={30} className="absolute right-4 top-4 cursor-pointer mt-2" />
                            <h2 className="text-2xl p-4 flex items-center">
                                <img src={andriodIcon} alt="Logo" className="w-8 h-8 mr-2 mt-2" />
                                <span className="font-bold text-black dark:text-white">UNITE</span>
                            </h2>

                            {/* Menu Items groupId = 1 */}

                            <nav data-testid="navbar-menu-items-group-1" className="relative">
                                <ul className="flex flex-col p-4">
                                    {filterMenuItems(1).map(({ icon, text, path, notAvailableOffline, id }) => {
                                        if (notAvailableOffline && !isOnline) {
                                            return (
                                                <div key={id} className="py-2 group text-gray-600" onClick={() => setNav(false)}>
                                                    <div className="items-center text-sm flex cursor-pointer mx-auto p-1 relative">
                                                        {icon} {text}
                                                        <div className="absolute right-0 top-0 h-full w-1 bg-orange-1000 opacity-0 group-hover:opacity-100 transition-opacity duration-100" />
                                                    </div>
                                                </div>)
                                        }
                                        return (
                                            <div key={id} className="py-2 group" onClick={() => setNav(false)}>
                                                <Link to={path} data-testid={`navbar-${id}-link`} className="items-center text-sm flex cursor-pointer mx-auto p-1 relative">
                                                    {icon} {text}
                                                    <div className="absolute right-0 top-0 h-full w-1 bg-orange-1000 opacity-0 group-hover:opacity-100 transition-opacity duration-100" />
                                                </Link>
                                            </div>
                                        )
                                    })}
                                </ul>
                            </nav>

                            <hr className="p-0" />

                            {/* Menu Items groupId = 2 */}

                            <nav data-testid="navbar-menu-items-group-2" className="relative">
                                <ul className="flex flex-col p-4 text-black dark:text-white">
                                    {filterMenuItems(2).map(({ icon, text, path, link, notAvailableOffline, id }, index) => {
                                        if (notAvailableOffline && !isOnline) {
                                            return (
                                                <div data-testid={`navbar-menu-item-${id}`} key={`admin-${index}-${text}`} className="py-2 group text-gray-600" onClick={() => setNav(false)}>
                                                    <div className="items-center text-sm flex cursor-pointer mx-auto p-1 relative">
                                                        {icon} {text}
                                                        <div className="absolute right-0 top-0 h-full w-1 bg-orange-1000 opacity-0 group-hover:opacity-100 transition-opacity duration-100" />
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div data-testid={`navbar-menu-item-${id}`} key={`admin-${index}-${text}`} className="py-2 group" onClick={() => setNav(false)}>
                                                {path ? (
                                                    <Link id={id} data-testid={`navbar-${id}-link`} to={path} className="items-center text-sm flex cursor-pointer mx-auto p-1 relative">
                                                        {icon} {text}
                                                        <div className="absolute right-0 top-0 h-full w-1 bg-orange-1000 opacity-0 group-hover:opacity-100 transition-opacity duration-100" />
                                                    </Link>
                                                ) : (
                                                    <a id={id} data-testid={`navbar-${id}-link`} href={link} target="_blank" rel="noopener noreferrer" className="items-center text-sm flex cursor-pointer mx-auto p-1 relative">
                                                        {icon} {text}
                                                        <div className="absolute right-0 top-0 h-full w-1 bg-orange-1000 opacity-0 group-hover:opacity-100 transition-opacity duration-100" />
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })}
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div >
    );
};

export default Navbar;